import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { TOrder } from '@onesource/schemas';
import {
  getMemberOrderByNoQueryFn,
  getMemberOrderByNoQueryKey,
  useGetMemberSubscriptionByNoQuery,
} from '@onesource/services';
import { useQueries } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CgArrowsExchange } from 'react-icons/cg';
import { HiArrowLeft, HiArrowRight, HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { CONFIG } from 'src/config-global';
import { useClassicAreaLink } from 'src/layouts/sidebar/use-classic-area-link';

import { PageContainer } from '#/components/page-container';
import { CHANGE_FIT_PLAN_URL, FIT_PRODUCT_ID, FIT_PRODUCT_URL } from '#/config';

// ----------------------------------------------------------------------

const centsToUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status.toLowerCase()) {
    case 'active': {
      return 'success';
    }
    case 'cancelled':
    case 'expired': {
      return 'error';
    }
    case 'pending': {
      return 'warning';
    }
    default: {
      return 'default';
    }
  }
};

const ORDERS_PER_PAGE = 10;

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso));

const orderStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active': {
      return 'success';
    }
    case 'failed':
    case 'cancelled':
    case 'expired': {
      return 'error';
    }
    case 'pending': {
      return 'warning';
    }
    default: {
      return 'default';
    }
  }
};

// ----------------------------------------------------------------------

export const SubscriptionDetailPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { subscriptionNo } = useParams({ from: '/subscriptions/$subscriptionNo' });
  const [ordersPage, setOrdersPage] = useState(1);

  const { data: subscription } = useGetMemberSubscriptionByNoQuery(subscriptionNo);
  const { handleClassicPanelClick } = useClassicAreaLink();

  const pagedOrderIds =
    subscription?.orderIds.slice((ordersPage - 1) * ORDERS_PER_PAGE, ordersPage * ORDERS_PER_PAGE) ?? [];

  const orderQueries = useQueries({
    queries: pagedOrderIds.map(id => ({
      queryKey: getMemberOrderByNoQueryKey(id),
      queryFn: () => getMemberOrderByNoQueryFn(id),
      enabled: !!subscription,
    })),
  });

  useEffect(() => {
    if (!subscription) {
      navigate({ to: '/billing', search: { tab: 'subscription' } });
    }
  }, [subscription, navigate]);

  if (!subscription) {
    return null;
  }

  const totalOrderPages = Math.ceil((subscription.orderIds.length ?? 0) / ORDERS_PER_PAGE);
  const ordersLoading = orderQueries.some(q => q.isLoading);
  const orders = orderQueries.map(q => q.data).filter(Boolean) as TOrder[];

  return (
    <>
      <Helmet>
        <title>{`Subscription #${subscription.subscriptionNo} - ${CONFIG.appName}`}</title>
      </Helmet>

      <PageContainer>
        <Container maxWidth="xl" sx={{ px: 0, py: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Link to="/billing" search={{ tab: 'subscription' }} style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{
                    minWidth: 0,
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                    borderRadius: '50%',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    boxShadow: 'none',
                    transition: 'background-color 0.2s, color 0.2s',
                    '&:hover': { bgcolor: 'primary.dark', color: '#fff', boxShadow: 'none' },
                  }}
                >
                  <HiArrowLeft size={22} />
                </Button>
              </Link>
              <Typography variant="h4" fontWeight={700}>
                Subscription #{subscription.subscriptionNo}
              </Typography>
            </Box>
            <Chip
              label={subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              color={statusColor(subscription.status)}
              sx={{ fontWeight: 600, fontSize: 13, px: 1 }}
            />
          </Box>

          {/* Info cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
            {[
              { label: 'Next Payment', value: subscription.nextPaymentOn },
              { label: 'Subtotal', value: centsToUSD(subscription.subtotal) },
              { label: 'Discount', value: centsToUSD(subscription.discount) },
              { label: 'Total', value: centsToUSD(subscription.total) },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2.5,
                  py: 2,
                  minWidth: { xs: '100%', md: 160 },
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography variant="caption" color="text.disabled" fontWeight={500} display="block" mb={0.5}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Products table */}
          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Qty
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscription.products.map(product => (
                  <TableRow key={product.productId} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      {product.productId === FIT_PRODUCT_ID ? (
                        <Box
                          onClick={() => handleClassicPanelClick(FIT_PRODUCT_URL)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {product.productName}
                          </Typography>
                          <HiArrowTopRightOnSquare size={14} />
                        </Box>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {product.productName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{product.quantity}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{centsToUSD(product.price)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {centsToUSD(product.price * product.quantity)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals summary */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 5,
              gap: 2,
            }}
          >
            {subscription.products.some(p => p.productId === FIT_PRODUCT_ID) ? (
              <Button
                variant="contained"
                color="warning"
                size="small"
                endIcon={<CgArrowsExchange style={{ marginRight: '15px' }} size={26} />}
                onClick={() => handleClassicPanelClick(CHANGE_FIT_PLAN_URL)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                }}
              >
                Change/Cancel Plan
              </Button>
            ) : (
              <Box />
            )}
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{centsToUSD(subscription.subtotal)}</Typography>
              </Box>
              {subscription.discount > 0 && (
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Discount
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    −{centsToUSD(subscription.discount)}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Total
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {centsToUSD(subscription.total)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Related Orders */}
          {subscription.orderIds.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Orders
              </Typography>
              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  mb: 2,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersLoading
                      ? pagedOrderIds.map(id => (
                          <TableRow key={id}>
                            <TableCell>
                              <Skeleton variant="text" width={72} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="rounded" width={80} height={24} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={56} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="rounded" width={64} height={28} />
                            </TableCell>
                          </TableRow>
                        ))
                      : orders.map(order => (
                          <TableRow key={order.orderNo} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                #{order.orderNo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(order.date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                color={orderStatusColor(order.status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {centsToUSD(order.total)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Link
                                to="/orders/$orderNo"
                                params={{ orderNo: String(order.orderNo) }}
                                style={{ textDecoration: 'none' }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  endIcon={<HiArrowRight size={16} />}
                                  sx={{
                                    minWidth: 88,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '& .MuiButton-endIcon': { marginRight: 0 },
                                  }}
                                >
                                  View
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalOrderPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalOrderPages}
                    page={ordersPage}
                    onChange={(_, page) => setOrdersPage(page)}
                    variant="outlined"
                    color="primary"
                    shape="rounded"
                    size="large"
                  />
                </Box>
              )}
            </Box>
          )}
        </Container>
      </PageContainer>
    </>
  );
};
